/* eslint-disable no-console */
import React, { useContext } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { MdOutlineFileUpload } from 'react-icons/md';
import { BiEdit } from 'react-icons/bi'; // Default edit icon
import { LanguageContext } from '../../../libs/Context';
import { RiDeleteBinLine } from 'react-icons/ri';

export interface UploadBoxProps {
	title: string;
	file: any;
	fileType: 'image' | 'video';
	fileRef: React.RefObject<HTMLInputElement>;
	handleClick: () => any;
	handleChange: () => any;
	handleDelete: () => any; // Add this prop
	uploadText: string;
	fileTypes: string;
	maxSize: string;
	editable: boolean;
	accept : string;
	width?: string;
	height?: string;
	editIconComponent?: React.ElementType;
	boxProps?: object;
}

const UploadBox: React.FC<UploadBoxProps> = ({
	title,
	file,
	fileType,
	fileRef,
	handleClick,
	handleChange,
	handleDelete, // Add this prop
	uploadText,
	fileTypes,
	maxSize,
	editable,
	width = 'auto',
	height = '170px',
	editIconComponent,
	accept,
	boxProps
}) =>
{
	const EditIcon = editIconComponent || BiEdit;
	// Default to BiEdit if no custom icon is provided
	const { language } = useContext(LanguageContext);

	return (
		<Stack direction='column' py='var(--pixel8)'>
			<Typography variant='inherit' fontWeight='var(--font500)' px='var(--pixel8)'>
				{title === `${language.introvideo}`
					? <>{language?.introvideo}
						<Typography variant='caption' fontWeight='var(--font500)' px='var(--pixel4)'>
							{language?.IntroVdoPlyBfrMeet}
						</Typography>
					</>
					: title
				}
			</Typography>
			<Box
				alignItems='center'
				justifyContent='center'
				display='flex'
				sx={{
					border                 : '1px dashed var(--grayA8)',
					borderRadius           : '13px',
					cursor                 : 'pointer',
					width,
					height,
					padding                : 'var(--pixel8)',
					margin                 : 'var(--pixel8)',
					position               : 'relative',
					'&:hover .delete-icon' : {
						display : 'block'
					},
					'& img, & video' : {
						width        : 'initial',
						height       : '150px',
						objectFit    : 'contain',
						borderRadius : 'var(--pixel8)',
						overflow     : 'hidden'
					},
					...boxProps
				}}
				onClick={handleClick}
			>
				{file
					? (
						<>
							{fileType === 'image'
								? (
									<img src={`${process.env.REACT_APP_S3_BASE_URL}/${file?.key}`} alt={title} />
								)
								: (
									<video width={width} height={height} controls={false}>
										<source src={`${process.env.REACT_APP_S3_BASE_URL}/${file?.key}`} type='video/mp4' />
										Your browser does not support the video tag.
									</video>
								)}
							{editable && (
								<Typography
									className='delete-icon'
									variant='inherit'
									color='textSecondary'
									sx={{
										display      : 'block',
										position     : 'absolute',
										top          : 'var(--pixel12)',
										right        : 'var(--pixel16)',
										cursor       : 'pointer',
										background   : 'white',
										borderRadius : 'var(--percent50)'
									}}
									onClick={(e) =>
									{
										e.stopPropagation();
										handleDelete();
									}}
								>
									<Tooltip title={language?.delete} placement='top'>
										<IconButton>
											<RiDeleteBinLine fontSize='var(--pixel20)' />
										</IconButton>
									</Tooltip>
								</Typography>
							)}
						</>
					)
					: (
						<Stack spacing={0.5} direction='column' alignItems='center' justifyContent='center'>
							<Typography variant='inherit' color='textSecondary'>
								<MdOutlineFileUpload fontSize='var(--pixel24)' />
							</Typography>
							<Typography variant='inherit' color='textSecondary'>
								{uploadText}
							</Typography>
							<Typography variant='caption' color='textSecondary'>
								{fileTypes}
							</Typography>
							<Typography variant='caption' color='textSecondary'>
								{maxSize}
							</Typography>
						</Stack>
					)}
			</Box>
			<input
				type='file'
				ref={fileRef}
				accept={accept}
				style={{ display: 'none' }}
				onChange={handleChange}
			/>
		</Stack>
	);
};

export default UploadBox;
